import { useContext, useState } from "react";
import { Box, Typography, Button, InputAdornment } from "@mui/material";
import { Lock, Person } from "@mui/icons-material";
import { Navigate, useNavigate } from "react-router-dom";

import NonLoggedInLayout from "../../layout/NonLoggedInLayout";
import CommonTextField from "../common/CommonTextField";
import AdminAuthContext from "../../context/AdminAuthContext";
import { adminLogin } from "../../services/authApi";
import { useToast } from "../../hook/useToast";

const getLoginErrorMessage = (error) => {
  const data = error?.response?.data;
  const errors = data?.errors || data;

  if (Array.isArray(errors?.non_field_errors)) {
    return errors.non_field_errors[0];
  }

  if (Array.isArray(errors?.detail)) {
    return errors.detail[0];
  }

  return errors?.detail || data?.message || "Invalid email or password";
};

const AdminLoginPage = () => {
  const { showToast } = useToast();
  const { loginAdmin, adminUser } = useContext(AdminAuthContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ email: "", password: "" });

  if (adminUser?.role === "Admin") {
    return <Navigate to="/admin-dashboard" replace />;
  }

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!formData.email || !formData.password) {
      showToast("Email and password are required", "error");
      return;
    }

    setLoading(true);
    try {
      const response = await adminLogin(formData);
      const accessToken = response?.data?.token?.access;

      if (!accessToken) {
        showToast("Login failed", "error");
        return;
      }

      loginAdmin(accessToken, response.data.user, response.data.dashboard);
      showToast("Admin login successful", "success");
      navigate("/admin-dashboard");
    } catch (error) {
      showToast(getLoginErrorMessage(error), "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <NonLoggedInLayout
      leftImage="/assets/images/admin-dashboard-login.png"
      leftImageAlt="Admin dashboard login"
    >
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 450,
            p: 4,
            borderRadius: "16px",
            backgroundColor: "#fff",
          }}
        >
          <Box sx={{ mb: 3 }}>
            <Typography
              sx={{
                fontSize: "36px",
                fontWeight: 700,
                color: "#2D315F",
                letterSpacing: 0,
                lineHeight: 1.1,
              }}
            >
              Admin
            </Typography>
            <Typography
              sx={{
                fontSize: "36px",
                fontWeight: 700,
                color: "#FFB04A",
                letterSpacing: 0,
                lineHeight: 1.1,
              }}
            >
              Portal
            </Typography>
            <Box
              component="span"
              sx={{
                display: "block",
                width: 60,
                height: 3,
                bgcolor: "#2D315F",
                mt: 1,
                borderRadius: 2,
              }}
            />
          </Box>

          <Typography
            sx={{
              fontSize: "14px",
              color: "rgba(129, 143, 171, 1)",
              mt: 1,
              mb: 3,
              fontWeight: 500,
            }}
          >
            Sign in to access the admin dashboard
          </Typography>

          <form onSubmit={handleSubmit}>
            <CommonTextField
              placeholder="E-mail"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              autoComplete="email"
              fullWidth
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Person />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#F5F5F5",
                  borderRadius: "12px",
                  height: "48px",
                  "& fieldset": { border: "none" },
                  "&:hover fieldset": { border: "none" },
                  "&.Mui-focused fieldset": { border: "none" },
                },
                "& .MuiOutlinedInput-input": { padding: "12px 14px" },
                mb: 1.5,
              }}
            />

            <CommonTextField
              placeholder="Password"
              name="password"
              type="password"
              value={formData.password}
              onChange={handleInputChange}
              autoComplete="current-password"
              fullWidth
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <Lock />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#F5F5F5",
                  borderRadius: "12px",
                  height: "48px",
                  "& fieldset": { border: "none" },
                  "&:hover fieldset": { border: "none" },
                  "&.Mui-focused fieldset": { border: "none" },
                },
                "& .MuiOutlinedInput-input": { padding: "12px 14px" },
                mb: 1,
              }}
            />

           

            <Button
              type="submit"
              disabled={loading}
              fullWidth
              sx={{
                mt:0.7,
                py: 1.2,
                borderRadius: "12px",
                backgroundColor: "#2D315F",
                color: "#fff",
                fontSize: "15px",
                fontWeight: 600,
                textTransform: "none",
                "&:hover": { backgroundColor: "#252957" },
              }}
            >
              {loading ? "Signing in..." : "Sign In"}
            </Button>
          </form>
        </Box>
      </Box>
    </NonLoggedInLayout>
  );
};

export default AdminLoginPage;
